/**
 * printPatientChart — build a comprehensive, print-ready patient record and
 * fire the browser print dialog through a hidden iframe (same pattern used
 * in Billing.jsx to avoid popup blockers and CSS clipping bugs).
 *
 * Includes: patient identity, vitals history, medications, MAR (if any),
 * SOAP notes with ICD-10 + attending doctor + signed timestamps, labs with
 * flags, imaging with reports, and a signature block. Empty sections are
 * skipped. Uganda PDPO 2019 footer + generated-by/at audit line included.
 */

const esc = (s) =>
  (s === null || s === undefined ? "" : String(s))
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return esc(iso);
    return d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return esc(iso); }
};
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return esc(iso);
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return esc(iso); }
};

// Adult physiological ranges — matches the vitals engine used elsewhere
const V_ALERT = {
  bp_sys:  (v) => v != null && (v < 90 || v > 180),
  pulse:   (v) => v != null && (v < 50 || v > 120),
  temp:    (v) => v != null && (v < 35 || v > 38.5),
  rr:      (v) => v != null && (v < 12 || v > 25),
  spo2:    (v) => v != null && v < 92,
  glucose: (v) => v != null && (v < 3.9 || v > 11.0),
};
const pill = (v, alert, unit = "") => {
  if (v === null || v === undefined || v === "") return "<span class=muted>—</span>";
  const cls = alert ? "abn" : "";
  return `<span class="${cls}">${esc(v)}${unit ? `<span class=muted> ${unit}</span>` : ""}</span>`;
};

const CATEGORY_COLOR = {
  normal: "#1B5E20", low: "#B45309", high: "#B45309", critical: "#B71C1C",
};

const flagChip = (flag) => {
  if (!flag) return "";
  const bg = CATEGORY_COLOR[flag] || "#334155";
  const label = flag[0].toUpperCase() + flag.slice(1);
  return `<span class="chip" style="background:${bg}1A;color:${bg};border:1px solid ${bg}55">${label}</span>`;
};

export function printPatientChart({ patient, vitals = [], prescriptions = [], notes = [], labs = [], imaging = [], mar = [], diagnoses = [], generatedBy, organization }) {
  if (!patient) return;

  const orgName = esc(organization?.name || "Vitality Medical Software");
  const now = new Date();
  const genLine = `${orgName} · Generated ${fmtDateTime(now.toISOString())}${generatedBy?.name ? ` by ${esc(generatedBy.name)}` : ""}${generatedBy?.role ? ` (${esc(generatedBy.role)})` : ""}`;

  // Sort helpers — newest first for tables, but SOAP notes are shown oldest → newest
  const byNew = (a, b) => new Date(b.created_at || b.recorded_at || b.taken_at || b.signed_at || 0) - new Date(a.created_at || a.recorded_at || a.taken_at || a.signed_at || 0);
  const byOld = (a, b) => new Date(a.created_at || a.signed_at || 0) - new Date(b.created_at || b.signed_at || 0);

  // ---- PATIENT HEADER ----
  const allergies = (patient.allergies || []).filter(Boolean);
  const patientHead = `
    <div class="pt-head">
      <div class="pt-name">${esc(patient.first_name || "")} ${esc(patient.middle_name || "")} ${esc(patient.last_name || "")}</div>
      <div class="pt-meta">
        <span><b>Patient ID:</b> ${esc(patient.ths_id || patient.patient_id)}</span>
        <span><b>Sex:</b> ${esc(patient.gender || "—")}</span>
        <span><b>DOB:</b> ${esc(patient.date_of_birth || "—")}${patient.estimated_age ? ` <span class=muted>(~${esc(patient.estimated_age)} yrs)</span>` : ""}</span>
        <span><b>Blood:</b> ${esc(patient.blood_group || "—")}</span>
        <span><b>Phone:</b> ${esc(patient.phone_primary || "—")}</span>
        <span><b>Location:</b> ${esc([patient.village, patient.parish, patient.subcounty, patient.district, patient.country].filter(Boolean).join(", ") || "—")}</span>
        ${patient.nationality ? `<span><b>Nationality:</b> ${esc(patient.nationality)}</span>` : ""}
        ${patient.national_id ? `<span><b>National ID:</b> ${esc(patient.national_id)}</span>` : ""}
        ${allergies.length ? `<span class="allergy-strong"><b>ALLERGIES:</b> ${esc(allergies.join(", "))}</span>` : `<span><b>Allergies:</b> None known</span>`}
        ${patient.insurance_provider ? `<span><b>Insurance:</b> ${esc(patient.insurance_provider)}${patient.insurance_member ? " · " + esc(patient.insurance_member) : ""}</span>` : ""}
      </div>
    </div>`;

  // ---- VITALS ----
  const vitalsSorted = [...vitals].sort(byNew);
  const vitalsSection = vitalsSorted.length ? `
    <section>
      <h2>Vitals · ${vitalsSorted.length} reading${vitalsSorted.length === 1 ? "" : "s"}</h2>
      <table class="grid">
        <thead><tr>
          <th>Date / time</th><th>BP</th><th>Pulse</th><th>Temp</th><th>RR</th><th>SpO₂</th><th>Glucose</th><th>Pain</th><th>Recorded by</th>
        </tr></thead>
        <tbody>
          ${vitalsSorted.map(v => `
            <tr>
              <td class="nowrap">${fmtDateTime(v.recorded_at || v.taken_at || v.created_at)}</td>
              <td>${pill(v.bp_sys && v.bp_dia ? `${v.bp_sys}/${v.bp_dia}` : "", V_ALERT.bp_sys(v.bp_sys), "mmHg")}</td>
              <td>${pill(v.pulse, V_ALERT.pulse(v.pulse), "bpm")}</td>
              <td>${pill(v.temperature, V_ALERT.temp(v.temperature), "°C")}</td>
              <td>${pill(v.respiratory_rate, V_ALERT.rr(v.respiratory_rate), "/min")}</td>
              <td>${pill(v.spo2, V_ALERT.spo2(v.spo2), "%")}</td>
              <td>${pill(v.glucose, V_ALERT.glucose(v.glucose), "mmol/L")}</td>
              <td>${pill(v.pain_score, false, "/10")}</td>
              <td class="muted">${esc(v.recorded_by_name || v.by_name || "—")}</td>
            </tr>
            ${v.notes ? `<tr><td colspan=9 class="sub muted">Note: ${esc(v.notes)}</td></tr>` : ""}
          `).join("")}
        </tbody>
      </table>
    </section>` : "";

  // ---- MEDICATIONS ----
  const rxSorted = [...prescriptions].sort(byNew);
  const rxSection = rxSorted.length ? `
    <section>
      <h2>Medications · ${rxSorted.length}</h2>
      <table class="grid">
        <thead><tr>
          <th>Date</th><th>Rx #</th><th>Medications</th><th>Status</th><th>Prescribed by</th>
        </tr></thead>
        <tbody>
          ${rxSorted.map(r => {
            const items = (r.items || []).map(i => `${esc(i.drug || i.medication || "—")} <span class="muted">${esc([i.dose, i.route, i.frequency, i.duration ? `× ${i.duration}` : "", i.quantity ? `qty ${i.quantity}` : ""].filter(Boolean).join(" · "))}</span>`).join("<br/>") || "<span class=muted>—</span>";
            return `
              <tr>
                <td class="nowrap">${fmtDate(r.created_at || r.prescribed_at)}</td>
                <td class="tabular muted">${esc(r.rx_number || r.prescription_id || "—")}</td>
                <td>${items}</td>
                <td>${esc(r.status || "—")}</td>
                <td class="muted">${esc(r.prescribed_by_name || r.doctor_name || "—")}</td>
              </tr>`;
          }).join("")}
        </tbody>
      </table>
    </section>` : "";

  // ---- MAR ----
  const marSorted = [...mar].sort(byNew);
  const marSection = marSorted.length ? `
    <section>
      <h2>Medication Administration Record (MAR) · ${marSorted.length} dose${marSorted.length === 1 ? "" : "s"}</h2>
      <table class="grid">
        <thead><tr>
          <th>Time given</th><th>Drug</th><th>Dose</th><th>Route</th><th>Administered by</th><th>Note</th>
        </tr></thead>
        <tbody>
          ${marSorted.map(m => `
            <tr>
              <td class="nowrap">${fmtDateTime(m.created_at || m.administered_at || m.given_at)}</td>
              <td>${esc(m.drug || m.medication || "—")}</td>
              <td>${esc(m.dose || "—")}</td>
              <td>${esc(m.route || "—")}</td>
              <td class="muted">${esc(m.recorded_by_name || m.administered_by_name || m.by_name || "—")}${m.action && m.action !== "Administered" ? ` <span class="chip" style="background:#fef3c7;color:#92400e;border:1px solid #fbbf24">${esc(m.action)}</span>` : ""}</td>
              <td class="muted">${esc([m.site, m.notes].filter(Boolean).join(" · "))}</td>
            </tr>
          `).join("")}
        </tbody>
      </table>
    </section>` : "";

  // ---- SOAP NOTES ----
  const notesSorted = [...notes].sort(byOld);
  const dash = `<span class="muted">—</span>`;
  const soapSection = notesSorted.length ? `
    <section>
      <h2>Consultation notes (SOAP) · ${notesSorted.length}</h2>
      ${notesSorted.map(n => `
        <div class="note">
          <div class="note-head">
            <div class="note-title">${esc(n.diagnosis || n.chief_complaint || n.title || "Consultation")}</div>
            <div class="note-meta">
              ${(n.icd_code || n.icd10_code) ? `<span class="pill blue"><b>ICD-10</b> ${esc(n.icd_code || n.icd10_code)}${(n.icd_name || n.icd10_description) ? " — " + esc(n.icd_name || n.icd10_description) : ""}</span>` : ""}
              <span class="pill ${(n.signed || n.signed_at) ? "green" : "amber"}">${(n.signed || n.signed_at) ? "Signed" : (n.status ? esc(n.status) : "Draft")}</span>
            </div>
          </div>
          <div class="note-sub">
            <b>${esc(n.signed_by_name || n.doctor_name || n.author_name || n.by_name || "—")}</b>${n.signed_at ? ` · signed ${fmtDateTime(n.signed_at)}` : ` · created ${fmtDateTime(n.created_at)}`}
          </div>
          <div class="soap">
            <div class="soap-row"><div class="soap-label">S — Subjective</div><div class="soap-body">${n.subjective ? esc(n.subjective) : dash}</div></div>
            <div class="soap-row"><div class="soap-label">O — Objective</div><div class="soap-body">${n.objective  ? esc(n.objective)  : dash}</div></div>
            <div class="soap-row"><div class="soap-label">A — Assessment</div><div class="soap-body">${n.assessment ? esc(n.assessment) : dash}</div></div>
            <div class="soap-row"><div class="soap-label">P — Plan</div><div class="soap-body">${n.plan       ? esc(n.plan)       : dash}</div></div>
          </div>
        </div>
      `).join("")}
    </section>` : "";

  // ---- DIAGNOSES (derived from the structured `diagnosis` + `icd_code` fields
  // already attached to each SOAP consultation — not a separate collection, not
  // parsed from Assessment prose). One row per consultation that has a diagnosis. ----
  const dxRows = notes
    .filter(n => (n.diagnosis || "").trim())
    .map(n => ({
      date: n.signed_at || n.created_at,
      description: (n.diagnosis || "").trim(),
      icd_code: n.icd_code || n.icd10_code || "",
      icd_name: n.icd_name || n.icd10_description || "",
      diagnosed_by_name: n.signed_by_name || n.author_name || n.doctor_name || n.by_name || "",
    }))
    .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
  const dxSection = `
    <section>
      <h2>Diagnosis · ${dxRows.length}</h2>
      ${dxRows.length === 0 ? `<div class="muted">No diagnosis recorded</div>` : `
        <table class="grid">
          <thead><tr>
            <th>Date</th><th>Diagnosis</th><th>ICD-10</th><th>Diagnosed by</th>
          </tr></thead>
          <tbody>
            ${dxRows.map(d => `
              <tr>
                <td class="nowrap">${d.date ? fmtDate(d.date) : "—"}</td>
                <td>${esc(d.description)}</td>
                <td class="tabular">${d.icd_code ? `${esc(d.icd_code)}${d.icd_name ? " — " + esc(d.icd_name) : ""}` : "—"}</td>
                <td class="muted">${esc(d.diagnosed_by_name || "—")}</td>
              </tr>
            `).join("")}
          </tbody>
        </table>
      `}
    </section>`;

  // ---- LABS ----
  const labsSorted = [...labs].sort(byNew);
  const labsSection = labsSorted.length ? `
    <section>
      <h2>Laboratory · ${labsSorted.length} order${labsSorted.length === 1 ? "" : "s"}</h2>
      ${labsSorted.map(o => `
        <div class="lab-order">
          <div class="lab-head">
            <div><b>${esc(o.test_names || (o.tests || []).map(t => t.name).join(", ") || "Lab order")}</b></div>
            <div class="muted">${fmtDateTime(o.created_at)} · Ordered by ${esc(o.ordered_by_name || o.doctor_name || "—")} · Status: ${esc(o.status || "—")}</div>
          </div>
          ${(o.results && o.results.length) ? `
            <table class="grid tight">
              <thead><tr><th>Test</th><th>Result</th><th>Unit</th><th>Reference</th><th>Flag</th></tr></thead>
              <tbody>
                ${o.results.map(r => `
                  <tr>
                    <td>${esc(r.name || r.test_name || "—")}</td>
                    <td class="tabular ${r.flag === "critical" ? "abn" : ""}"><b>${esc(r.value ?? "—")}</b></td>
                    <td class="muted">${esc(r.unit || "")}</td>
                    <td class="muted">${esc(r.reference_range || r.range || "")}</td>
                    <td>${flagChip(r.flag)}</td>
                  </tr>
                `).join("")}
              </tbody>
            </table>
          ` : `<div class="muted">No results recorded yet</div>`}
          ${o.verified_by_name ? `<div class="muted sm">Verified by ${esc(o.verified_by_name)} on ${fmtDateTime(o.verified_at)}</div>` : ""}
        </div>
      `).join("")}
    </section>` : "";

  // ---- IMAGING ----
  const imgSorted = [...imaging].sort(byNew);
  const imgSection = imgSorted.length ? `
    <section>
      <h2>Imaging · ${imgSorted.length} order${imgSorted.length === 1 ? "" : "s"}</h2>
      <table class="grid">
        <thead><tr>
          <th>Date</th><th>Modality</th><th>Study</th><th>Status</th><th>Reported by</th><th>Report</th>
        </tr></thead>
        <tbody>
          ${imgSorted.map(o => `
            <tr>
              <td class="nowrap">${fmtDate(o.created_at)}</td>
              <td><b>${esc(o.modality || "—")}</b></td>
              <td>${esc(o.study || o.title || o.body_part || "—")}</td>
              <td>${esc(o.status || "—")}</td>
              <td class="muted">${esc(o.reported_by_name || o.radiologist_name || "—")}</td>
              <td class="muted">${esc(o.report_summary || o.report || o.impression || "").slice(0, 400)}</td>
            </tr>
          `).join("")}
        </tbody>
      </table>
    </section>` : "";

  // ---- ATTENDING PHYSICIAN SUMMARY (unique names from notes + orders) ----
  const attendingSet = new Set();
  notes.forEach(n => { const nm = n.doctor_name || n.author_name || n.by_name; if (nm) attendingSet.add(nm); });
  prescriptions.forEach(r => { const nm = r.prescribed_by_name || r.doctor_name; if (nm) attendingSet.add(nm); });
  labs.forEach(o => { const nm = o.ordered_by_name || o.doctor_name; if (nm) attendingSet.add(nm); });
  imaging.forEach(o => { const nm = o.ordered_by_name || o.doctor_name; if (nm) attendingSet.add(nm); });
  const attendings = Array.from(attendingSet);
  const attendingLine = attendings.length ? `<div class="attending"><b>Attending doctor${attendings.length > 1 ? "s" : ""}:</b> ${esc(attendings.join(" · "))}</div>` : "";

  // Sections list (only non-empty)
  const anyClinical = [vitalsSection, rxSection, marSection, soapSection, dxSection, labsSection, imgSection].some(Boolean);

  const signatureBlock = `
    <section class="signature">
      <div class="sig-row">
        <div class="sig-cell">
          <div class="sig-line"></div>
          <div class="sig-label">Attending doctor — signature &amp; stamp</div>
          <div class="sig-sub">${esc(generatedBy?.name || "")}${generatedBy?.role === "doctor" ? " · Dr" : ""}</div>
        </div>
        <div class="sig-cell">
          <div class="sig-line"></div>
          <div class="sig-label">Date</div>
        </div>
      </div>
    </section>`;

  const html = `<!doctype html>
<html><head><meta charset="utf-8"><title>Patient record · ${esc(patient.first_name || "")} ${esc(patient.last_name || "")} · ${esc(patient.ths_id || patient.patient_id)}</title>
<style>
  @page { size: A4; margin: 16mm 14mm 18mm 14mm; }
  @page :first { margin-top: 12mm; }
  * { box-sizing: border-box; }
  html, body { font-family: 'Helvetica', 'Arial', sans-serif; font-size: 10.5pt; color: #0f172a; line-height: 1.4; }
  body { margin: 0; }
  .brand { display:flex; justify-content:space-between; align-items:flex-end; border-bottom: 2px solid #0b3d91; padding-bottom: 8pt; margin-bottom: 10pt; }
  .brand h1 { font-size: 15pt; color: #0b3d91; font-weight: 800; letter-spacing: 0.3pt; margin:0; }
  .brand .doc-type { font-size: 8pt; letter-spacing: 3pt; text-transform: uppercase; color:#475569; }
  .brand .gen-line { font-size: 8pt; color: #475569; text-align: right; }
  .pt-head { border: 1.5px solid #0b3d91; border-radius: 6pt; padding: 8pt 10pt; margin-bottom: 12pt; background: #f8fafc; page-break-inside: avoid; }
  .pt-name { font-size: 14pt; font-weight: 800; color:#0b3d91; margin-bottom: 4pt; }
  .pt-meta { display: flex; flex-wrap: wrap; gap: 8pt 18pt; font-size: 9pt; color: #334155; }
  .pt-meta b { color: #0f172a; font-weight: 600; }
  .allergy-strong { color: #B71C1C !important; font-weight: 700; }
  .attending { margin: 4pt 0 10pt 0; font-size: 9pt; color: #0b3d91; }
  section { margin-bottom: 12pt; page-break-inside: auto; }
  section h2 { font-size: 12pt; color: #0b3d91; margin: 10pt 0 6pt 0; padding-bottom: 3pt; border-bottom: 1px solid #cbd5e1; page-break-after: avoid; }
  table.grid { width: 100%; border-collapse: collapse; font-size: 9pt; }
  table.grid.tight { font-size: 8.5pt; }
  table.grid th { text-align: left; background: #0b3d91; color: #fff; padding: 4pt 6pt; font-weight: 600; }
  table.grid td { padding: 4pt 6pt; border-bottom: 1px solid #e2e8f0; vertical-align: top; }
  table.grid tbody tr { page-break-inside: avoid; }
  .muted { color: #64748b; }
  .sm { font-size: 8pt; }
  .nowrap { white-space: nowrap; }
  .tabular { font-variant-numeric: tabular-nums; }
  .sub { font-size: 8.5pt; padding-left: 8pt !important; }
  .abn { color: #B71C1C; font-weight: 700; }
  .chip { display:inline-block; padding: 1pt 6pt; border-radius: 999pt; font-size: 8pt; font-weight: 600; }
  .note { border: 1px solid #e2e8f0; border-radius: 6pt; padding: 8pt 10pt; margin-bottom: 8pt; page-break-inside: avoid; background:#fff; }
  .note-head { display:flex; justify-content:space-between; align-items:flex-start; gap:8pt; margin-bottom: 3pt; }
  .note-title { font-weight: 700; color:#0b3d91; font-size: 10.5pt; }
  .note-meta { display:flex; gap:5pt; flex-wrap:wrap; }
  .pill { display:inline-block; padding: 1pt 6pt; border-radius: 999pt; font-size: 8pt; font-weight: 600; }
  .pill.blue { background:#dbeafe; color:#0b3d91; }
  .pill.green { background:#dcfce7; color:#166534; }
  .pill.amber { background:#fef3c7; color:#92400e; }
  .note-sub { font-size: 8.5pt; color:#475569; margin-bottom: 4pt; }
  .soap { display:grid; grid-template-columns: 78pt 1fr; row-gap: 3pt; column-gap: 8pt; font-size: 9pt; }
  .soap-row { display:contents; }
  .soap-label { font-weight:700; color:#0b3d91; font-size: 8.5pt; padding-top: 2pt; }
  .soap-body { white-space: pre-wrap; }
  .lab-order { border-left: 3px solid #0EA5B7; padding: 4pt 8pt; margin-bottom: 8pt; background:#f8fafc; page-break-inside: avoid; }
  .lab-head { display:flex; justify-content:space-between; align-items:flex-end; margin-bottom: 3pt; }
  .signature { margin-top: 16pt; page-break-inside: avoid; }
  .sig-row { display:flex; gap: 24pt; }
  .sig-cell { flex: 1; }
  .sig-line { border-top: 1px solid #0f172a; margin-top: 30pt; }
  .sig-label { font-size: 8.5pt; color:#334155; text-align:center; padding-top: 3pt; font-weight: 600; }
  .sig-sub { font-size: 8pt; color: #64748b; text-align:center; }
  .foot { border-top: 1px solid #e2e8f0; margin-top: 12pt; padding-top: 6pt; font-size: 7.5pt; color: #64748b; text-align:center; }
  .empty { color: #64748b; font-style: italic; text-align: center; padding: 12pt; }
</style></head><body>
  <div class="brand">
    <div>
      <h1>${orgName}</h1>
      <div class="doc-type">Comprehensive patient record</div>
    </div>
    <div class="gen-line">${esc(genLine)}</div>
  </div>
  ${patientHead}
  ${attendingLine}
  ${anyClinical
    ? vitalsSection + rxSection + marSection + soapSection + dxSection + labsSection + imgSection
    : `<div class="empty">No clinical data recorded for this patient yet.</div>`}
  ${signatureBlock}
  <div class="foot">
    Confidential medical record — Vitality Medical Software (VMS). Under the Uganda Data Protection and Privacy Act 2019, this document may only be shared with authorised parties.
  </div>
</body></html>`;

  // Build hidden iframe, write into it, print, then clean up. Same pattern as
  // Billing statement print (safer than window.open — no popup blocker, no CSS clip bugs).
  const prev = document.getElementById("vms-print-frame");
  if (prev) prev.remove();
  const iframe = document.createElement("iframe");
  iframe.id = "vms-print-frame";
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;";
  document.body.appendChild(iframe);
  iframe.onload = () => {
    try {
      const cw = iframe.contentWindow;
      cw.focus();
      cw.print();
    } catch (e) {
      console.error("Patient chart print failed", e);
    }
    const cleanup = () => { const el = document.getElementById("vms-print-frame"); if (el) el.remove(); };
    try { iframe.contentWindow.addEventListener("afterprint", cleanup); } catch {}
    setTimeout(cleanup, 60000);
  };
  iframe.srcdoc = html;
}
